import { useCallback } from 'react';
import { MaybeLoanID } from 'src/common/types';
import { useUnauthorizedAccessGuard } from 'src/services/graphql/hooks/useUnauthorizedAccessGuard';
import { useSendToFirstParty } from 'src/experiences/MortgageDashboard/hooks/useSendToFirstParty/useSendToFirstParty';
import { useStartBlend } from '../../../hooks/useStartBlend/useStartBlend';
import { ServerSideModuleData } from '../../../GetServerSideProps/utils/getServerSideModuleData';
import { UpsellState } from '../types';
import { UpsellCTAVariant } from '../anticorruption/UpsellCTAVariant';
import { useUpsellComputedState } from './useUpsellComputedState';
import { useUpsellAnalytics } from './useUpsellAnalytics';

export const useUpsellState = ({
    loanID,
    serverFetchedData,
}: {
    loanID: MaybeLoanID;
    serverFetchedData: Pick<ServerSideModuleData, 'Upsell'>;
}): UpsellState => {
    const {
        isLoading,
        error,
        variant,
        currentState,
        lead_id,
        isFirstPartyExperience,
        borrower_id,
        documentURL,
        downloadURL,
        street,
        valuation,
        purchasePrice,
        experienceURL,
        firstPartyPreApprovalDetailsURL,
    } = useUpsellComputedState({ loanID, serverFetchedData });

    const { trackPrimaryCTAClick, trackSecondaryCTAClick } = useUpsellAnalytics({
        lead_id,
        currentState,
        loading: isLoading,
        isFirstPartyExperience,
    });

    useUnauthorizedAccessGuard(error);
    const { sendToBlend } = useStartBlend(loanID, borrower_id);
    const { sendToFirstParty } = useSendToFirstParty(loanID, borrower_id);
    const onPrimaryCTAClick = useCallback(() => {
        switch (variant) {
            case UpsellCTAVariant.FIRST_PARTY_NEW_APPLICATION:
            case UpsellCTAVariant.FIRST_PARTY_VERIFIED_PREAPPROVAL_NO_TRID_OUTSTANDING_TASKS:
            case UpsellCTAVariant.FIRST_PARTY_OUTSTANDING_TASKS:
            case UpsellCTAVariant.FIRST_PARTY_WAITING_TO_CLOSE:
                sendToFirstParty(experienceURL);
                break;
            case UpsellCTAVariant.FIRST_PARTY_VERIFIED_PREAPPROVAL:
                sendToFirstParty(firstPartyPreApprovalDetailsURL);
                break;
            case UpsellCTAVariant.NEW_APPLICATION:
            case UpsellCTAVariant.OUTSTANDING_TASKS: {
                // Third-party Outstanding Tasks sends to Blend
                sendToBlend();
                break;
            }
            case UpsellCTAVariant.VERIFIED_PREAPPROVAL:
                // Primary CTA for Preapproval views the letter, which is handled by the presentational layer
                break;
            case UpsellCTAVariant.WAITING_TO_CLOSE: {
                // Third-party Waiting to Close sends to Blend
                sendToBlend();
                break;
            }
            default:
                // No primary CTA for default case. When we have client side DD logging, we should log this
                break;
        }
    }, [variant, sendToFirstParty, sendToBlend, experienceURL, firstPartyPreApprovalDetailsURL]);

    const onSecondaryCTAClick = useCallback(() => {
        switch (variant) {
            case UpsellCTAVariant.FIRST_PARTY_VERIFIED_PREAPPROVAL_NO_TRID_OUTSTANDING_TASKS:
                sendToFirstParty(firstPartyPreApprovalDetailsURL);
                break;
            case UpsellCTAVariant.OUTSTANDING_TASKS:
            // Outstanding Tasks has no secondary CTA
            case UpsellCTAVariant.FIRST_PARTY_OUTSTANDING_TASKS:
            // First Party Outstanding Tasks has no secondary CTA
            case UpsellCTAVariant.WAITING_TO_CLOSE:
            // Waiting to close has no secondary CTA
            case UpsellCTAVariant.FIRST_PARTY_WAITING_TO_CLOSE:
            // First Party Waiting to Close has no secondary CTA
            case UpsellCTAVariant.FIRST_PARTY_VERIFIED_PREAPPROVAL:
            // First Party Verified Pre-Approval has no secondary CTA
            case UpsellCTAVariant.VERIFIED_PREAPPROVAL:
            // Verified Pre-Approval has no secondary CTA
            case UpsellCTAVariant.NEW_APPLICATION:
            // New Application secondary CTA is a phone link handled by the Browser
            default:
                break;
        }
    }, [variant, sendToFirstParty, firstPartyPreApprovalDetailsURL]);

    return {
        variant,
        isLoading,
        onPrimaryCTAClick,
        trackPrimaryCTAClick,
        onSecondaryCTAClick,
        trackSecondaryCTAClick,
        documentURL,
        downloadURL,
        valuation,
        purchasePrice,
        address: street,
        loanID,
    };
};
